import React, { useState, useEffect, useRef } from 'react';
import { Icons } from '../components/ui/Icon';
import { BRAND } from '../config/brand';
import {
    LOGIN_SERVER_UNAVAILABLE_MESSAGE,
    requestLoginWorkspaceBrand,
    requestQuoteMakerLogin,
    workspaceDomainFromEmail,
} from '../services/login-auth';
import { retryCountdownText, secondsUntil } from '../utils/retry-countdown';
import type { LoginTenantChoice, LoginWorkspaceBrand } from '../services/login-auth';
import { DEFAULT_ACCENT, deriveAccentStops } from '../services/brand-accent';
import './LoginPage.css';

// ============================================================================
// LoginPage. POSTs to /api/auth/login, stores JWT, then hard-reloads.
// ----------------------------------------------------------------------------
// Retired (2026-04-21 / v5.11.28): legacy SHA-256-over-localStorage dict + the
// "Quick Access" admin/staff buttons that only mutated localStorage without
// getting a real backend JWT. On a fresh Win11 Customer Zero install this path
// confused users: they typed real credentials into a form doing hash
// comparison against two hardcoded legacy users (`admin`/`password`,
// `assessor`/`password`), got "Invalid username or password", while the
// silent devAutoLogin() in data-service-api.js had already succeeded in the
// background: the form just didn't know. Replaced with a thin wrapper
// around /api/auth/login so what you type is what the backend validates.
// App.jsx now gates on sessionStorage.qm_access_token (JWT presence), so
// devAutoLogin's silent success flips the app to logged-in automatically;
// this form only renders when auto-login fails (offline backend, bad creds,
// or a non-localhost deployment without baked-in dev creds).
// ----------------------------------------------------------------------------
// Company Workspace Foyer (2026-08-30).
//
// The desktop keeps the established split between QuoteMaker identity and the
// sign-in task. On a phone the brand pane becomes a compact masthead so the
// form, not a full-screen marketing panel, owns the first viewport.
//
// Hosting location is deliberately absent. The earlier LOCAL / HOST / On
// device tiles were hardcoded presentation, not runtime evidence, and became
// false in the cloud. The page now says only what is true in every supported
// deployment: a work email identifies the company workspace and authentication
// remains owned by /api/auth/login.
// ============================================================================

const QM_LOGIN_CONFIRM_MS = 480;
// Outage lane, 2026-09-29: while QuoteMaker's server is updating, the form
// tries the same sign-in again on this backoff instead of calling the answer a
// wrong password. Editing the email or password stops it.
const QM_LOGIN_SERVER_WAIT_DELAYS_MS = [3000, 6000, 12000, 20000, 30000];
const QM_WORKSPACE_LOOKUP_MS = 320;
// Before an email matches a workspace the page wears the platform default,
// never the colour of the business that last signed in on this device (G2,
// use-tenant-session-verification.ts): a shared device must not show one
// business's brand to the next person who signs in.

function loginCooldownMessage(seconds: number) {
    const minutes = Math.max(1, Math.ceil(seconds / 60));
    return `Sign-in is temporarily paused. Try again in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`;
}

function loginCooldownClock(seconds: number) {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
}

type WorkspacePreviewState =
    | { kind: 'idle' }
    | { kind: 'loading'; domain: string }
    | { kind: 'matched'; domain: string; workspace: LoginWorkspaceBrand }
    | { kind: 'ambiguous'; domain: string }
    | { kind: 'unknown'; domain: string }
    | { kind: 'unavailable'; domain: string };

export default function LoginPage(_props: { onLogin?: (user: unknown) => void }) {
    // Operator 2026-08-14: the field used to hardcode admin@rjbird.com.au —
    // wrong the moment a second person exists. Prefill with the last address
    // that successfully signed in ON THIS DEVICE (written below on success),
    // otherwise blank. localStorage, not sessionStorage: remembering across
    // sessions is the point.
    const [email, setEmail] = useState(() => {
        try { return localStorage.getItem('qm_last_login_email') || ''; } catch { return ''; }
    });
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [confirmed, setConfirmed] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showHelp, setShowHelp] = useState(false);
    const [mounted, setMounted] = useState(false);
    const [tenantChoices, setTenantChoices] = useState<LoginTenantChoice[]>([]);
    const [selectedTenantSlug, setSelectedTenantSlug] = useState('');
    const [tenantSelectionMessage, setTenantSelectionMessage] = useState('');
    const firstTenantChoiceRef = useRef<HTMLButtonElement | null>(null);
    const [workspacePreview, setWorkspacePreview] = useState<WorkspacePreviewState>({ kind: 'idle' });
    const [cooldownEndsAt, setCooldownEndsAt] = useState(0);
    const [cooldownSeconds, setCooldownSeconds] = useState(0);
    const [cooldownReadyMessage, setCooldownReadyMessage] = useState('');
    const loginRequestInFlightRef = useRef(false);
    const reloadRef = useRef<any>(null);
    const [serverWait, setServerWait] = useState<{ attempt: number; nextAt: number } | null>(null);
    const [serverWaitNow, setServerWaitNow] = useState(() => Date.now());
    const serverWaitTimerRef = useRef<number | null>(null);
    const serverWaitAttemptRef = useRef(0);
    const submitLoginRef = useRef<() => Promise<void>>(async () => {});

    const clearServerWaitTimer = () => {
        if (serverWaitTimerRef.current !== null) window.clearTimeout(serverWaitTimerRef.current);
        serverWaitTimerRef.current = null;
    };
    const cancelServerWait = () => {
        clearServerWaitTimer();
        serverWaitAttemptRef.current = 0;
        setServerWait(null);
    };

    useEffect(() => {
        requestAnimationFrame(() => setMounted(true));
    }, []);

    const workspaceDomain = workspaceDomainFromEmail(email);

    useEffect(() => {
        if (!workspaceDomain) {
            setWorkspacePreview({ kind: 'idle' });
            return undefined;
        }

        const controller = new AbortController();
        let current = true;
        setWorkspacePreview({ kind: 'loading', domain: workspaceDomain });

        const timer = window.setTimeout(async () => {
            try {
                const result = await requestLoginWorkspaceBrand(workspaceDomain, {
                    signal: controller.signal,
                });
                if (current) setWorkspacePreview({ ...result, domain: workspaceDomain });
            } catch (lookupError) {
                if (current && !(lookupError instanceof DOMException && lookupError.name === 'AbortError')) {
                    setWorkspacePreview({ kind: 'unavailable', domain: workspaceDomain });
                }
            }
        }, QM_WORKSPACE_LOOKUP_MS);

        return () => {
            current = false;
            window.clearTimeout(timer);
            controller.abort();
        };
    }, [workspaceDomain]);

    useEffect(() => {
        if (tenantChoices.length > 0) firstTenantChoiceRef.current?.focus();
    }, [tenantChoices]);

    useEffect(() => {
        setCooldownReadyMessage('');
        // A retry must never send credentials the operator has since changed.
        cancelServerWait();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [email, password]);

    useEffect(() => () => clearServerWaitTimer(), []);

    useEffect(() => {
        if (!serverWait) return undefined;
        setServerWaitNow(Date.now());
        const timer = window.setInterval(() => setServerWaitNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [serverWait]);

    useEffect(() => {
        if (!cooldownEndsAt) return undefined;

        const refreshCooldown = () => {
            const remaining = Math.max(0, Math.ceil((cooldownEndsAt - Date.now()) / 1000));
            setCooldownSeconds(remaining);
            if (remaining === 0) {
                setCooldownEndsAt(0);
                setError('');
                setCooldownReadyMessage('You can sign in now.');
            }
        };

        refreshCooldown();
        const timer = window.setInterval(refreshCooldown, 1000);
        return () => window.clearInterval(timer);
    }, [cooldownEndsAt]);

    // Deliberately NO unmount cleanup for reloadRef (v5.50.7). The old
    // clearTimeout-on-unmount cancelled the scheduled hard reload the moment
    // anything unmounted this page. A prior onLogin() path DID unmount it, so
    // the reload never fired and the pre-login App instance kept every stale
    // boot-time useState snapshot (symptom: the "install password" banner
    // still showing after signing in with a freshly-changed password,
    // operator 2026-08-05). A window-level reload needs no React lifecycle
    // cancellation: the only unmount path with a pending timer is a
    // devAutoLogin race, where reloading into the logged-in app is harmless.

    const clearTenantSelection = () => {
        setTenantChoices([]);
        setSelectedTenantSlug('');
        setTenantSelectionMessage('');
    };

    const handleSubmit = (e: any) => {
        e.preventDefault();
        void submitLoginRef.current();
    };

    const submitLogin = async () => {
        if (cooldownSeconds > 0 || loginRequestInFlightRef.current) return;
        clearServerWaitTimer();
        setError('');
        setCooldownReadyMessage('');
        if (tenantChoices.length > 0 && !selectedTenantSlug) {
            setError('Choose your company workspace to continue.');
            return;
        }
        loginRequestInFlightRef.current = true;
        setLoading(true);

        try {
            const result = await requestQuoteMakerLogin({
                email,
                password,
                tenantSlug: selectedTenantSlug || undefined,
            });

            if (result.kind === 'tenant-selection') {
                setTenantChoices(result.choices);
                setSelectedTenantSlug('');
                setTenantSelectionMessage(result.message);
                setLoading(false);
                return;
            }

            if (result.kind === 'error' && result.serverUnavailable) {
                // Nothing judged the credentials: the server is updating or
                // unreachable. Keep them, say so, and try again by ourselves.
                const attempt = serverWaitAttemptRef.current;
                serverWaitAttemptRef.current = attempt + 1;
                const delay = QM_LOGIN_SERVER_WAIT_DELAYS_MS[Math.min(attempt, QM_LOGIN_SERVER_WAIT_DELAYS_MS.length - 1)]!;
                setError('');
                setServerWait({ attempt: attempt + 1, nextAt: Date.now() + delay });
                serverWaitTimerRef.current = window.setTimeout(() => {
                    serverWaitTimerRef.current = null;
                    void submitLoginRef.current();
                }, delay);
                setLoading(false);
                return;
            }
            serverWaitAttemptRef.current = 0;
            setServerWait(null);

            if (result.kind === 'error') {
                if (result.status === 429) {
                    const retryAfterSeconds = result.retryAfterSeconds || 60;
                    setCooldownSeconds(retryAfterSeconds);
                    setCooldownEndsAt(Date.now() + (retryAfterSeconds * 1000));
                    setError(loginCooldownMessage(retryAfterSeconds));
                } else {
                    setError(result.message || 'Invalid email or password');
                }
                setLoading(false);
                return;
            }

            const data = result.payload;

            // Store tokens the same keys data-service-api.js uses.
            sessionStorage.setItem('qm_access_token', data.accessToken);
            if (data.refreshToken) sessionStorage.setItem('qm_refresh_token', data.refreshToken);
            // Bootstrap-credential flag survives the hard-reload below; App
            // renders the set-your-own-password strip while it is present.
            if (data.usingBootstrapPassword) {
                sessionStorage.setItem('qm_bootstrap_pw_login', '1');
            } else {
                sessionStorage.removeItem('qm_bootstrap_pw_login');
            }

            // Signature: the CTA becomes a "Signed in" check for ~480ms so the
            // confirmation lands under the cursor, then we hard-reload.
            //
            // The reload is the ONLY transition out of this screen (v5.50.7).
            // It re-boots App.jsx so every boot-time useState initializer
            // (user claims, the bootstrap-password banner flag, the
            // session-resume receipt and tenant-local database binding)
            // re-reads the sessionStorage this submit just wrote. No in-place
            // onLogin fallback is safe here: reload() schedules navigation and
            // returns, so that fallback can authenticate the pre-login App
            // instance while it is still bound to the quarantine database.
            // Remember the address that just worked, for next time's prefill.
            try { localStorage.setItem('qm_last_login_email', email.trim()); } catch { /* private mode */ }
            setConfirmed(true);
            reloadRef.current = setTimeout(() => {
                window.location.reload();
            }, QM_LOGIN_CONFIRM_MS);
        } catch (err) {
            // "Is the backend running?" asked a question no builder or site
            // assessor can answer, at the front door of the product, at the
            // moment they are already blocked, with no button to press. An
            // error message that ends in a question the reader cannot resolve
            // is a dead end. Say what to do instead, in the order to do it.
            serverWaitAttemptRef.current = 0;
            setServerWait(null);
            setError('QuoteMaker cannot reach the sign-in service. Wait a moment and try again. If it keeps failing, contact your administrator.');
            setLoading(false);
        } finally {
            loginRequestInFlightRef.current = false;
        }
    };
    submitLoginRef.current = submitLogin;

    const waitingForCompany = tenantChoices.length > 0 && !selectedTenantSlug;
    const coolingDown = cooldownSeconds > 0;
    const matchedWorkspace = tenantChoices.length === 0 && workspacePreview.kind === 'matched'
        ? workspacePreview.workspace
        : null;
    const workspaceAccent = matchedWorkspace?.primaryColor || DEFAULT_ACCENT;
    const accentStops = deriveAccentStops(workspaceAccent);
    const loginStyle = {
        '--qm-login-accent': accentStops['--qm-accent'],
        '--qm-login-accent-hover': accentStops['--qm-accent-deep'],
        '--qm-login-accent-pressed': accentStops['--qm-accent-pressed'],
        '--qm-login-on-accent': accentStops['--qm-accent-contrast'],
    } as React.CSSProperties;
    const workspaceState = tenantChoices.length > 0 ? 'selection' : workspacePreview.kind;

    return (
        <div
            className="qm-login"
            data-qm-surface="login"
            data-qm-login-layout="split"
            data-mounted={mounted ? 'true' : 'false'}
            data-workspace-state={workspaceState}
            style={loginStyle}
        >
            {/* A div with banner semantics avoids the global header treatment. */}
            <div
                role="banner"
                className="qm-login__brand"
                data-qm-login-panel="brand"
            >
                <div className="qm-login__brand-top" data-qm-surface="login-stamp">
                    <img
                        className="qm-login__logo"
                        src="/assets/qm-logo-white.png"
                        alt="QuoteMaker Pty Ltd"
                        width={989}
                        height={288}
                    />
                </div>

                {!matchedWorkspace && (
                    <div className="qm-login__pitch" data-qm-login-pitch="t">
                        <h1 className="qm-login__title">
                            Every job quoted.
                            <br />
                            In your own words.
                        </h1>
                        <p className="qm-login__lede">
                            Turn site photos, RFQs and job files into a priced scope
                            and a quote written in your company&rsquo;s voice.
                        </p>
                    </div>
                )}
            </div>

            {matchedWorkspace && (
                <section
                    className="qm-login__tenant-identity"
                    data-qm-login-tenant-identity="t"
                    role="status"
                    aria-live="polite"
                >
                    <h1 className="qm-login__tenant-name">{matchedWorkspace.name}</h1>
                    <p className="qm-login__tenant-copy">Secure access to your QuoteMaker workspace.</p>
                </section>
            )}

            <div
                className="qm-login__form-panel"
                data-qm-login-panel="form"
                aria-labelledby="qm-login-heading"
            >
                <div className="qm-login__form-shell">
                    <h2 className="qm-login__heading" id="qm-login-heading">Sign in</h2>
                    <p className="qm-login__intro">
                        Use your work email and password to continue.
                    </p>

                    <form onSubmit={handleSubmit}>
                        <div className="qm-login__field">
                            <label
                                className="qm-login__label"
                                htmlFor="qm-login-email"
                            >
                                Work email
                            </label>
                            <input
                                className="qm-login__input"
                                id="qm-login-email"
                                type="email"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    clearTenantSelection();
                                }}
                                placeholder="name@company.com.au"
                                autoComplete="email"
                                aria-describedby="qm-login-email-hint"
                                required
                            />
                        </div>

                        {tenantChoices.length > 0 ? (
                            <div
                                className="qm-login__company-note qm-login__company-note--selection"
                                data-qm-login-company="selection"
                            >
                                <p className="visually-hidden" role="status" aria-live="assertive">
                                    {tenantChoices.length} company workspaces found. Choose one to continue.
                                </p>
                                <span className="qm-login__company-icon" aria-hidden="true">
                                    <Icons.Building size={18} />
                                </span>
                                <div>
                                    <p className="qm-login__company-title" id="qm-login-company-heading">
                                        Choose your company
                                    </p>
                                    <p className="qm-login__company-copy" id="qm-login-email-hint">
                                        {tenantSelectionMessage || 'These details match more than one company workspace.'}
                                    </p>
                                </div>
                                <div
                                    className="qm-login__company-options"
                                    role="group"
                                    aria-labelledby="qm-login-company-heading"
                                >
                                    {tenantChoices.map((choice, index) => {
                                        const selected = selectedTenantSlug === choice.slug;
                                        return (
                                            <button
                                                className="qm-login__company-option"
                                                data-selected={selected ? 'true' : 'false'}
                                                data-qm-login-company-option={choice.slug}
                                                key={choice.slug}
                                                ref={index === 0 ? firstTenantChoiceRef : undefined}
                                                type="button"
                                                aria-pressed={selected}
                                                onClick={() => {
                                                    setSelectedTenantSlug(choice.slug);
                                                    setError('');
                                                }}
                                            >
                                                <span>{choice.name}</span>
                                                {selected && <Icons.Check size={16} aria-hidden="true" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            <div
                                className="qm-login__email-status"
                                data-qm-login-company={workspacePreview.kind}
                                role="status"
                                aria-live="polite"
                                aria-busy={workspacePreview.kind === 'loading'}
                            >
                                <span className="qm-login__email-status-icon" aria-hidden="true">
                                    {workspacePreview.kind === 'matched'
                                        ? <Icons.Check size={14} />
                                        : <Icons.Building size={14} />}
                                </span>
                                <p className="qm-login__email-status-copy" id="qm-login-email-hint">
                                    {workspacePreview.kind === 'loading' && `Finding the workspace for @${workspacePreview.domain}\u2026`}
                                    {workspacePreview.kind === 'matched' && `Workspace matched from @${workspacePreview.domain}.`}
                                    {workspacePreview.kind === 'ambiguous' && 'Sign in to choose between matching workspaces.'}
                                    {workspacePreview.kind === 'unknown' && 'Your company will be confirmed securely when you sign in.'}
                                    {workspacePreview.kind === 'unavailable' && 'Company matching is unavailable. You can still sign in.'}
                                    {workspacePreview.kind === 'idle' && 'Enter your work email to match your company.'}
                                </p>
                            </div>
                        )}

                        <div className="qm-login__field qm-login__field--password">
                            <div className="qm-login__label-row">
                                <label className="qm-login__label" htmlFor="qm-login-password">
                                    Password
                                </label>
                                <button
                                    className="qm-login__text-button"
                                    type="button"
                                    onClick={() => setShowHelp((v) => !v)}
                                    aria-expanded={showHelp}
                                    aria-controls="qm-login-help"
                                >
                                    Need password help?
                                </button>
                            </div>
                            <div className="qm-login__password-wrap">
                                <input
                                    className="qm-login__input qm-login__input--password"
                                    id="qm-login-password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => {
                                        setPassword(e.target.value);
                                        clearTenantSelection();
                                    }}
                                    placeholder="Enter your password"
                                    autoComplete="current-password"
                                    required
                                />
                                <button
                                    className="qm-login__password-toggle"
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword
                                        ? <Icons.EyeOff size={17} aria-hidden="true" />
                                        : <Icons.Eye size={17} aria-hidden="true" />}
                                </button>
                            </div>
                        </div>

                        {showHelp && (
                            <p
                                className="qm-login__help"
                                id="qm-login-help"
                            >
                                Ask a company administrator to reset your password from Settings,
                                Users.
                            </p>
                        )}

                        {error && (
                            <div
                                className="qm-login__alert"
                                role="alert"
                            >
                                <Icons.AlertTriangle size={15} aria-hidden="true" /> {error}
                            </div>
                        )}

                        {serverWait && (
                            <div
                                className="qm-login__server-wait"
                                data-qm-login-server-wait="t"
                                role="status"
                                aria-live="polite"
                            >
                                <p className="qm-login__server-wait-text">{LOGIN_SERVER_UNAVAILABLE_MESSAGE}</p>
                                <div className="qm-login__server-wait-row">
                                    <span className="qm-login__server-wait-count">
                                        {retryCountdownText(secondsUntil(serverWait.nextAt, serverWaitNow))}
                                    </span>
                                    <button
                                        type="button"
                                        className="qm-login__server-wait-try"
                                        onClick={() => { void submitLoginRef.current(); }}
                                        disabled={loading}
                                    >
                                        Try now
                                    </button>
                                </div>
                            </div>
                        )}

                        {coolingDown && (
                            <p
                                className="qm-login__cooldown"
                                data-qm-login-countdown="t"
                                aria-live="off"
                            >
                                Available in <strong>{loginCooldownClock(cooldownSeconds)}</strong>
                            </p>
                        )}

                        <p
                            className="visually-hidden"
                            data-qm-login-ready="t"
                            role="status"
                            aria-live="polite"
                        >
                            {cooldownReadyMessage}
                        </p>

                        <button
                            className="qm-login__cta"
                            type="submit"
                            data-qm-login-cta="t"
                            data-auth-state={confirmed ? 'confirmed' : loading ? 'loading' : coolingDown ? 'cooldown' : waitingForCompany ? 'waiting' : 'ready'}
                            disabled={loading || confirmed || coolingDown || waitingForCompany}
                        >
                            {confirmed ? (
                                <>
                                    <Icons.Check size={17} aria-hidden="true" /> Signed in
                                </>
                            ) : loading ? (
                                <>
                                    <span className="qm-login__spinner" aria-hidden="true" />
                                    Signing in&hellip;
                                </>
                            ) : coolingDown ? (
                                'Sign-in paused'
                            ) : (
                                <>
                                    {tenantChoices.length > 0 ? 'Continue' : 'Sign in'}
                                    <Icons.ArrowRight size={16} aria-hidden="true" />
                                </>
                            )}
                        </button>
                    </form>

                    <p className="qm-login__version">QuoteMaker v{BRAND.version}</p>
                </div>
            </div>
        </div>
    );
}
